import { Link } from "react-router-dom";
import DramaCard from "@/components/feature/DramaCard";
import type { Drama } from "@/mocks/dramas";

interface SectionHeaderProps {
  title: string;
  subtitle?: string;
  moreTo?: string;
  moreLabel?: string;
}

export function SectionHeader({ title, subtitle, moreTo, moreLabel = "查看全部" }: SectionHeaderProps) {
  return (
    <div className="mb-5 flex items-end justify-between gap-4">
      <div>
        <h2 className="flex items-center gap-2 font-heading text-xl font-black text-foreground-950 md:text-2xl">
          <span className="h-5 w-1 rounded-full bg-primary-600" />
          {title}
        </h2>
        {subtitle ? <p className="mt-1.5 text-xs text-foreground-600 md:text-sm">{subtitle}</p> : null}
      </div>
      {moreTo ? (
        <Link
          to={moreTo}
          className="flex shrink-0 items-center gap-1 text-xs text-foreground-600 transition-colors hover:text-primary-600 cursor-pointer md:text-sm"
        >
          {moreLabel}
          <i className="ri-arrow-right-line" />
        </Link>
      ) : null}
    </div>
  );
}

interface DramaSectionProps {
  title: string;
  subtitle?: string;
  moreTo?: string;
  moreLabel?: string;
  items: Drama[];
  showRank?: boolean;
  columnsClassName?: string;
  emptyText?: string;
}

export default function DramaSection({
  title,
  subtitle,
  moreTo,
  moreLabel,
  items,
  showRank = false,
  columnsClassName = "grid-cols-2 sm:grid-cols-3 lg:grid-cols-6",
  emptyText = "暂无相关内容",
}: DramaSectionProps) {
  return (
    <section className="w-full">
      <SectionHeader title={title} subtitle={subtitle} moreTo={moreTo} moreLabel={moreLabel} />
      {items.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 py-14">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-inbox-line text-xl" />
          </span>
          <p className="mt-3 text-sm text-foreground-600">{emptyText}</p>
        </div>
      ) : (
        <div className={`grid gap-x-3 gap-y-6 md:gap-x-4 ${columnsClassName}`}>
          {items.map((item, index) => (
            <div key={item.id} className="relative">
              {showRank ? (
                <span className="absolute -left-1 -top-1 z-10 flex h-6 w-6 items-center justify-center rounded-md bg-foreground-950 text-[11px] font-bold text-background-50">
                  {index + 1}
                </span>
              ) : null}
              <DramaCard
                id={item.id}
                title={item.title}
                poster={item.poster}
                year={item.year}
                category={item.category}
                genres={item.genres}
                rating={item.rating}
                tags={item.tags}
                badge={item.category === "tv" ? "剧集" : undefined}
              />
            </div>
          ))}
        </div>
      )}
    </section>
  );
}